import { SEG_DIST, SPEED, STEER, type Chain } from './centipede-parts';

export type { ChainState } from './centipede-parts';

/**
 * Centipede movement (docs/02 B2 — no longer the Serpent's lunge): a wide
 * figure-eight sweep on the surface, then it BURROWS — the head flashes and
 * digs in, the chain travels underground (faint, untouchable) toward a dust
 * MOUND raised where the ship stood, and ERUPTS there with a burst.
 */
export const BURROW = {
  /** Surface time between burrows (÷ the chain's speed factor) */
  interval: 9.0,
  firstAt: 0.55,
  /** Head flashes and slows while it digs in (the tell) */
  dig: 0.55,
  digSlow: 0.4,
  /** Underground travel — straight to the mound */
  speed: 480,
  /** The mound shows at least this long before the eruption (fairness) */
  minUnder: 0.75,
  /** Surfacing: slowed while it bursts out */
  erupt: 0.35,
  eruptSlow: 0.3,
  burst: 10,
  burstSpeed: 170,
  moundR: 46,
  /** Underground silhouette alpha */
  alpha: 0.22,
  sweepX: 430,
  sweepY: 210,
  sweepRate: 0.5,
} as const;

/**
 * One movement step for a chain (head + follow-the-leader body).
 * @param f speed factor (short-chain bonus × tier move ramp)
 * @returns true on the step the chain erupts (boss fires the burst)
 */
export function stepChain(
  c: Chain, dt: number, px: number, py: number, homeX: number, homeY: number, f: number,
): boolean {
  const head = c.nodes[0]!;
  head.prevX = head.x;
  head.prevY = head.y;
  let erupted = false;
  let tx = head.x;
  let ty = head.y;
  let maxSpeed = SPEED * f;

  switch (c.state) {
    case 'surface':
      c.phase += BURROW.sweepRate * f * c.dir * dt;
      tx = homeX + Math.cos(c.phase) * BURROW.sweepX;
      ty = homeY + Math.sin(c.phase * 2) * BURROW.sweepY;
      c.burrowTimer -= dt;
      if (c.burrowTimer <= 0) {
        c.burrowTimer = BURROW.interval / f;
        c.state = 'dig';
        c.stateT = BURROW.dig;
      }
      break;
    case 'dig':
      tx = head.x + c.vx;
      ty = head.y + c.vy;
      maxSpeed *= BURROW.digSlow;
      c.stateT -= dt;
      if (c.stateT <= 0) {
        // Lock the mound on the ship's spot — it shows until the eruption
        c.state = 'under';
        c.stateT = 0;
        c.tx = px;
        c.ty = py;
      }
      break;
    case 'under': {
      c.stateT += dt;
      const dx = c.tx - head.x;
      const dy = c.ty - head.y;
      const d = Math.hypot(dx, dy);
      const step = Math.min(d, BURROW.speed * f * dt);
      if (d > 0) {
        c.vx = (dx / d) * BURROW.speed * f;
        c.vy = (dy / d) * BURROW.speed * f;
        head.x += (dx / d) * step;
        head.y += (dy / d) * step;
      }
      if (d - step < 1 && c.stateT >= BURROW.minUnder) {
        c.state = 'erupt';
        c.stateT = BURROW.erupt;
        erupted = true;
      }
      follow(c);
      return erupted;
    }
    case 'erupt':
      maxSpeed *= BURROW.eruptSlow;
      tx = head.x + c.vx;
      ty = head.y + c.vy;
      c.stateT -= dt;
      if (c.stateT <= 0) c.state = 'surface';
      break;
  }

  const dx = tx - head.x;
  const dy = ty - head.y;
  const dist = Math.hypot(dx, dy) || 1;
  c.vx += (dx / dist) * maxSpeed * STEER * dt;
  c.vy += (dy / dist) * maxSpeed * STEER * dt;
  const v = Math.hypot(c.vx, c.vy);
  if (v > maxSpeed) {
    c.vx = (c.vx / v) * maxSpeed;
    c.vy = (c.vy / v) * maxSpeed;
  }
  head.x += c.vx * dt;
  head.y += c.vy * dt;
  follow(c);
  return erupted;
}

/** Follow-the-leader body. */
function follow(c: Chain): void {
  let leadX = c.nodes[0]!.x;
  let leadY = c.nodes[0]!.y;
  for (let i = 1; i < c.nodes.length; i++) {
    const seg = c.nodes[i]!;
    seg.prevX = seg.x;
    seg.prevY = seg.y;
    const sx = leadX - seg.x;
    const sy = leadY - seg.y;
    const d = Math.hypot(sx, sy);
    if (d > SEG_DIST) {
      const pull = (d - SEG_DIST) / d;
      seg.x += sx * pull;
      seg.y += sy * pull;
    }
    leadX = seg.x;
    leadY = seg.y;
  }
}
